'use client'

import { motion } from 'framer-motion'
import { BackgroundBeams } from '../../../_components/_ui/background-beams'
import Link from 'next/link'

export default function AdvancedPage() {
    return (
        <div className="min-h-screen w-full relative">
            <BackgroundBeams />
            <div className="relative z-10 container mx-auto px-4 py-16">
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5 }}
                    className="max-w-5xl mx-auto"
                >
                    <div className="mb-8">
                        <Link href="/support/documentation" className="text-indigo-400 hover:text-indigo-300 text-sm">
                            ← Back to Documentation
                        </Link>
                    </div>

                    <h1 className="text-4xl font-bold text-white mb-6">
                        Advanced Features & Platform Extensibility
                    </h1>
                    
                    <div className="prose prose-lg prose-invert max-w-none">
                        <p className="text-gray-300 text-lg mb-8">
                            Beyond the core functionality, Autofocus offers advanced features including real-time 
                            collaboration, comprehensive analytics, powerful admin tools, and extensibility options 
                            for custom integrations and enterprise use cases.
                        </p>

                        {/* Real-time Features */}
                        <div className="bg-neutral-900/50 backdrop-blur-sm rounded-lg p-6 mb-8 border border-neutral-800">
                            <h2 className="text-2xl font-bold text-white mb-4">
                                ⚡ Real-time Collaboration
                            </h2>
                            <div className="space-y-6">
                                <div>
                                    <h3 className="text-lg font-semibold text-cyan-400 mb-3">Live Content Updates</h3>
                                    <p className="text-gray-300 text-sm mb-4">
                                        Powered by Supabase real-time subscriptions, Autofocus enables live collaboration 
                                        where multiple users can see content changes instantly across all connected clients.
                                    </p>
                                    <div className="grid md:grid-cols-2 gap-4">
                                        <div>
                                            <h4 className="font-semibold text-green-400 mb-2">Real-time Capabilities</h4>
                                            <ul className="text-gray-300 text-sm space-y-1">
                                                <li>• Live block creation and editing</li>
                                                <li>• Instant content synchronization</li>
                                                <li>• Real-time block reordering</li>
                                                <li>• Live user presence indicators</li>
                                                <li>• Collaborative checklist updates</li>
                                            </ul>
                                        </div>
                                        <div>
                                            <h4 className="font-semibold text-purple-400 mb-2">Technical Implementation</h4>
                                            <pre className="bg-neutral-800 p-3 rounded text-xs text-gray-300 overflow-x-auto">
{`// Real-time subscription setup
useEffect(() => {
  const subscription = supabase
    .channel(\`content_blocks:page_id=eq.\${pageId}\`)
    .on('postgres_changes', {
      event: '*',
      schema: 'public',
      table: 'content_blocks',
      filter: \`page_id=eq.\${pageId}\`
    }, (payload) => {
      if (payload.eventType === 'INSERT') {
        setBlocks(prev => [...prev, payload.new])
      } else if (payload.eventType === 'UPDATE') {
        setBlocks(prev => prev.map(block => 
          block.id === payload.new.id ? payload.new : block
        ))
      }
    })
    .subscribe()
    
  return () => subscription.unsubscribe()
}, [pageId])`}
                                            </pre>
                                        </div>
                                    </div>
                                </div>
                                
                                <div>
                                    <h3 className="text-lg font-semibold text-orange-400 mb-3">Conflict Resolution</h3>
                                    <div className="grid md:grid-cols-2 gap-4">
                                        <div>
                                            <h4 className="font-semibold text-blue-400 mb-2">Optimistic Updates</h4>
                                            <p className="text-gray-300 text-sm mb-2">
                                                Changes appear instantly for the user making them, with automatic rollback if conflicts occur.
                                            </p>
                                            <div className="bg-neutral-800 p-2 rounded text-xs text-gray-400">
                                                UI responds immediately → Server validation → Conflict resolution
                                            </div>
                                        </div>
                                        <div>
                                            <h4 className="font-semibold text-yellow-400 mb-2">Last-Write-Wins</h4>
                                            <p className="text-gray-300 text-sm mb-2">
                                                Simple conflict resolution where the most recent change takes precedence.
                                            </p>
                                            <div className="bg-neutral-800 p-2 rounded text-xs text-gray-400">
                                                Timestamp-based resolution with user notification
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Analytics & Insights */}
                        <div className="bg-neutral-900/50 backdrop-blur-sm rounded-lg p-6 mb-8 border border-neutral-800">
                            <h2 className="text-2xl font-bold text-white mb-4">
                                📊 Analytics & User Insights
                            </h2>
                            <div className="space-y-6">
                                <div>
                                    <h3 className="text-lg font-semibold text-indigo-400 mb-3">Multi-Tier Analytics System</h3>
                                    <p className="text-gray-300 text-sm mb-4">
                                        Comprehensive analytics tailored to subscription tiers, providing insights into 
                                        content performance, user engagement, and growth metrics.
                                    </p>
                                    
                                    <div className="grid md:grid-cols-3 gap-4">
                                        <div className="bg-neutral-800 p-4 rounded">
                                            <h4 className="font-semibold text-gray-400 mb-3">Basic Analytics</h4>
                                            <ul className="text-gray-300 text-sm space-y-1">
                                                <li>• Page view counts</li>
                                                <li>• Total visitors</li>
                                                <li>• Basic demographics</li>
                                                <li>• Simple time-based charts</li>
                                            </ul>
                                        </div>
                                        <div className="bg-neutral-800 p-4 rounded">
                                            <h4 className="font-semibold text-blue-400 mb-3">Turbo Analytics</h4>
                                            <ul className="text-gray-300 text-sm space-y-1">
                                                <li>• Block-level engagement</li>
                                                <li>• Referrer tracking</li>
                                                <li>• Geographic insights</li>
                                                <li>• Device and browser data</li>
                                                <li>• Custom date ranges</li>
                                            </ul>
                                        </div>
                                        <div className="bg-neutral-800 p-4 rounded">
                                            <h4 className="font-semibold text-purple-400 mb-3">Summit Analytics</h4>
                                            <ul className="text-gray-300 text-sm space-y-1">
                                                <li>• Real-time visitor tracking</li>
                                                <li>• Conversion funnel analysis</li>
                                                <li>• A/B testing capabilities</li>
                                                <li>• Custom event tracking</li>
                                                <li>• Advanced segmentation</li>
                                                <li>• Export and API access</li>
                                            </ul>
                                        </div>
                                    </div>
                                </div>
                                
                                <div>
                                    <h3 className="text-lg font-semibold text-green-400 mb-3">Block-Level Analytics</h3>
                                    <div className="grid md:grid-cols-2 gap-4">
                                        <div>
                                            <h4 className="font-semibold text-orange-400 mb-2">Engagement Metrics</h4>
                                            <pre className="bg-neutral-800 p-3 rounded text-xs text-gray-300 overflow-x-auto">
{`// Block analytics tracking
const trackBlockInteraction = async (blockId, eventType) => {
  await supabase.from('block_analytics').insert({
    block_id: blockId,
    event_type: eventType,  // 'view', 'click', 'share'
    timestamp: new Date(),
    user_agent: navigator.userAgent,
    referrer: document.referrer,
    session_id: getSessionId()
  })
}

// Usage tracking
<ImageBlock 
  onView={() => trackBlockInteraction(id, 'view')}
  onClick={() => trackBlockInteraction(id, 'click')}
/>`}
                                            </pre>
                                        </div>
                                        <div>
                                            <h4 className="font-semibold text-cyan-400 mb-2">Performance Insights</h4>
                                            <ul className="text-gray-300 text-sm space-y-1">
                                                <li>• Most engaging content types</li>
                                                <li>• Time spent on each block</li>
                                                <li>• Scroll depth analytics</li>
                                                <li>• Click-through rates</li>
                                                <li>• Share and interaction metrics</li>
                                                <li>• Mobile vs desktop engagement</li>
                                            </ul>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Admin Tools */}
                        <div className="bg-neutral-900/50 backdrop-blur-sm rounded-lg p-6 mb-8 border border-neutral-800">
                            <h2 className="text-2xl font-bold text-white mb-4">
                                🛠️ Admin Dashboard & Tools
                            </h2>
                            <div className="space-y-6">
                                <div>
                                    <h3 className="text-lg font-semibold text-red-400 mb-3">Comprehensive Admin Panel</h3>
                                    <p className="text-gray-300 text-sm mb-4">
                                        Full-featured admin dashboard providing complete control over users, content, 
                                        roles, and platform settings with detailed analytics and moderation tools.
                                    </p>
                                    
                                    <div className="grid md:grid-cols-2 gap-4">
                                        <div>
                                            <h4 className="font-semibold text-purple-400 mb-2">User Management</h4>
                                            <ul className="text-gray-300 text-sm space-y-1">
                                                <li>• Complete user directory with search</li>
                                                <li>• Role assignment and modification</li>
                                                <li>• Account suspension and reactivation</li>
                                                <li>• Subscription tier management</li>
                                                <li>• User activity monitoring</li>
                                                <li>• Bulk operations support</li>
                                            </ul>
                                        </div>
                                        <div>
                                            <h4 className="font-semibold text-orange-400 mb-2">Content Moderation</h4>
                                            <ul className="text-gray-300 text-sm space-y-1">
                                                <li>• Content review queue</li>
                                                <li>• Automated flagging system</li>
                                                <li>• Page and block visibility controls</li>
                                                <li>• Report management</li>
                                                <li>• Content approval workflows</li>
                                                <li>• Moderation audit logs</li>
                                            </ul>
                                        </div>
                                    </div>
                                </div>
                                
                                <div>
                                    <h3 className="text-lg font-semibold text-blue-400 mb-3">Role & Permission Management</h3>
                                    <div className="bg-neutral-800 p-4 rounded">
                                        <h4 className="font-semibold text-green-400 mb-2">Dynamic Role Assignment</h4>
                                        <pre className="bg-neutral-900 p-3 rounded text-xs text-gray-300 overflow-x-auto">
{`// Admin role management interface
const RoleManager = () => {
  const assignRole = async (userId, roleName, expiresAt = null) => {
    const response = await fetch('/api/admin/assign-role', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        userId,
        roleName,
        expiresAt
      })
    })
    
    if (response.ok) {
      showToast('Role assigned successfully', 'success')
      refreshUserList()
    }
  }
  
  return (
    <AdminPanel>
      <UserTable 
        users={users}
        onRoleChange={assignRole}
        onSuspend={suspendUser}
        onDelete={deleteUser}
      />
    </AdminPanel>
  )
}`}
                                        </pre>
                                    </div>
                                </div>
                                
                                <div>
                                    <h3 className="text-lg font-semibold text-yellow-400 mb-3">Platform Analytics</h3>
                                    <div className="grid md:grid-cols-3 gap-4">
                                        <div className="bg-neutral-800 p-3 rounded text-center">
                                            <div className="text-2xl font-bold text-blue-400">1,247</div>
                                            <div className="text-sm text-gray-400">Total Users</div>
                                        </div>
                                        <div className="bg-neutral-800 p-3 rounded text-center">
                                            <div className="text-2xl font-bold text-green-400">8,932</div>
                                            <div className="text-sm text-gray-400">Pages Created</div>
                                        </div>
                                        <div className="bg-neutral-800 p-3 rounded text-center">
                                            <div className="text-2xl font-bold text-purple-400">45,291</div>
                                            <div className="text-sm text-gray-400">Content Blocks</div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Platform Extensibility */}
                        <div className="bg-neutral-900/50 backdrop-blur-sm rounded-lg p-6 mb-8 border border-neutral-800">
                            <h2 className="text-2xl font-bold text-white mb-4">
                                🔌 Platform Extensibility
                            </h2>
                            <div className="space-y-6">
                                <div>
                                    <h3 className="text-lg font-semibold text-cyan-400 mb-3">Custom Block Development</h3>
                                    <p className="text-gray-300 text-sm mb-4">
                                        Autofocus's modular architecture allows for easy creation of custom block types 
                                        to meet specific business needs or unique use cases.
                                    </p>
                                    
                                    <div className="space-y-4">
                                        <div>
                                            <h4 className="font-semibold text-green-400 mb-2">Block Development Framework</h4>
                                            <pre className="bg-neutral-800 p-3 rounded text-xs text-gray-300 overflow-x-auto">
{`// Custom block template
export default function CustomBlock({ id, content, isEditing, onEdit, onSave }) {
  if (isEditing) {
    return <CustomBlockEditor content={content} onSave={onSave} />
  }
  
  return (
    <div className="custom-block" data-block-id={id}>
      <CustomBlockRenderer content={content} />
    </div>
  )
}

// Block registration
import { registerBlock } from '@/lib/blocks'

registerBlock('custom_type', {
  component: CustomBlock,
  editComponent: CustomBlockEditor,
  schema: customBlockSchema,
  icon: CustomIcon,
  name: 'Custom Block',
  description: 'A custom block for specific functionality'
})`}
                                            </pre>
                                        </div>
                                        
                                        <div>
                                            <h4 className="font-semibold text-purple-400 mb-2">Available Extension Points</h4>
                                            <div className="grid md:grid-cols-2 gap-4">
                                                <ul className="text-gray-300 text-sm space-y-1">
                                                    <li>• Custom block types with full rendering control</li>
                                                    <li>• Plugin system for additional functionality</li>
                                                    <li>• Theme and styling customization</li>
                                                    <li>• Webhook integrations for external services</li>
                                                </ul>
                                                <ul className="text-gray-300 text-sm space-y-1">
                                                    <li>• Custom API endpoints</li>
                                                    <li>• Third-party authentication providers</li>
                                                    <li>• Custom analytics integrations</li>
                                                    <li>• White-label deployment options</li>
                                                </ul>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                
                                <div>
                                    <h3 className="text-lg font-semibold text-orange-400 mb-3">API Integration & Webhooks</h3>
                                    <div className="grid md:grid-cols-2 gap-4">
                                        <div>
                                            <h4 className="font-semibold text-blue-400 mb-2">Webhook Events</h4>
                                            <pre className="bg-neutral-800 p-3 rounded text-xs text-gray-300 overflow-x-auto">
{`// Available webhook events
const webhookEvents = [
  'user.created',
  'user.updated', 
  'page.published',
  'block.created',
  'block.updated',
  'subscription.changed',
  'analytics.milestone'
]

// Webhook payload example
{
  "event": "page.published",
  "timestamp": "2024-01-01T00:00:00Z",
  "data": {
    "user_id": "uuid",
    "page_id": "uuid",
    "page_slug": "my-page",
    "username": "creator"
  }
}`}
                                            </pre>
                                        </div>
                                        <div>
                                            <h4 className="font-semibold text-yellow-400 mb-2">Integration Examples</h4>
                                            <ul className="text-gray-300 text-sm space-y-1">
                                                <li>• Slack notifications for new signups</li>
                                                <li>• Email automation with Mailchimp</li>
                                                <li>• Custom CRM synchronization</li>
                                                <li>• Social media cross-posting</li>
                                                <li>• Analytics data to business intelligence tools</li>
                                                <li>• Custom payment processing integration</li>
                                            </ul>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Performance Monitoring */}
                        <div className="bg-neutral-900/50 backdrop-blur-sm rounded-lg p-6 mb-8 border border-neutral-800">
                            <h2 className="text-2xl font-bold text-white mb-4">
                                📈 Advanced Performance Features
                            </h2>
                            <div className="space-y-6">
                                <div>
                                    <h3 className="text-lg font-semibold text-indigo-400 mb-3">Core Web Vitals Optimization</h3>
                                    <div className="grid md:grid-cols-3 gap-4">
                                        <div className="bg-neutral-800 p-4 rounded">
                                            <h4 className="font-semibold text-green-400 mb-2">LCP (Largest Contentful Paint)</h4>
                                            <div className="text-2xl font-bold text-green-400 mb-1">&lt; 1.2s</div>
                                            <ul className="text-gray-300 text-xs space-y-1">
                                                <li>• Image optimization and lazy loading</li>
                                                <li>• Critical CSS inlining</li>
                                                <li>• Resource preloading</li>
                                            </ul>
                                        </div>
                                        <div className="bg-neutral-800 p-4 rounded">
                                            <h4 className="font-semibold text-blue-400 mb-2">FID (First Input Delay)</h4>
                                            <div className="text-2xl font-bold text-blue-400 mb-1">&lt; 50ms</div>
                                            <ul className="text-gray-300 text-xs space-y-1">
                                                <li>• Code splitting and lazy loading</li>
                                                <li>• Debounced user interactions</li>
                                                <li>• Optimistic UI updates</li>
                                            </ul>
                                        </div>
                                        <div className="bg-neutral-800 p-4 rounded">
                                            <h4 className="font-semibold text-purple-400 mb-2">CLS (Cumulative Layout Shift)</h4>
                                            <div className="text-2xl font-bold text-purple-400 mb-1">&lt; 0.05</div>
                                            <ul className="text-gray-300 text-xs space-y-1">
                                                <li>• Reserved space for dynamic content</li>
                                                <li>• Consistent image sizing</li>
                                                <li>• Predictable component layouts</li>
                                            </ul>
                                        </div>
                                    </div>
                                </div>
                                
                                <div>
                                    <h3 className="text-lg font-semibold text-orange-400 mb-3">Advanced Caching Strategy</h3>
                                    <div className="grid md:grid-cols-2 gap-4">
                                        <div>
                                            <h4 className="font-semibold text-cyan-400 mb-2">Multi-Layer Caching</h4>
                                            <div className="bg-neutral-800 p-3 rounded text-sm">
                                                <div className="space-y-2 text-gray-300">
                                                    <div><span className="text-blue-400">Browser:</span> Service worker + localStorage</div>
                                                    <div><span className="text-green-400">CDN:</span> Edge caching with smart invalidation</div>
                                                    <div><span className="text-purple-400">API:</span> Redis caching for frequent queries</div>
                                                    <div><span className="text-orange-400">Database:</span> Query result caching</div>
                                                </div>
                                            </div>
                                        </div>
                                        <div>
                                            <h4 className="font-semibold text-red-400 mb-2">Smart Invalidation</h4>
                                            <pre className="bg-neutral-800 p-3 rounded text-xs text-gray-300 overflow-x-auto">
{`// Cache invalidation strategy
const invalidateCache = async (event) => {
  const patterns = {
    'block.updated': [\`page:\${pageId}\`, \`user:\${userId}\`],
    'page.published': [\`user:\${userId}\`, 'public:*'],
    'user.updated': [\`user:\${userId}\`, \`profile:\${userId}\`]
  }
  
  const cacheKeys = patterns[event.type] || []
  await Promise.all(
    cacheKeys.map(key => cache.invalidate(key))
  )
}`}
                                            </pre>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Enterprise Features */}
                        <div className="bg-neutral-900/50 backdrop-blur-sm rounded-lg p-6 mb-8 border border-neutral-800">
                            <h2 className="text-2xl font-bold text-white mb-4">
                                🏢 Enterprise & White-label Options
                            </h2>
                            <div className="space-y-6">
                                <div>
                                    <h3 className="text-lg font-semibold text-blue-400 mb-3">Enterprise Deployment</h3>
                                    <div className="grid md:grid-cols-2 gap-4">
                                        <div>
                                            <h4 className="font-semibold text-green-400 mb-2">Self-Hosted Options</h4>
                                            <ul className="text-gray-300 text-sm space-y-1">
                                                <li>• Docker containerization</li>
                                                <li>• Kubernetes deployment manifests</li>
                                                <li>• Custom domain and branding</li>
                                                <li>• On-premise database integration</li>
                                                <li>• Enterprise SSO integration</li>
                                                <li>• Custom security policies</li>
                                            </ul>
                                        </div>
                                        <div>
                                            <h4 className="font-semibold text-purple-400 mb-2">White-label Features</h4>
                                            <ul className="text-gray-300 text-sm space-y-1">
                                                <li>• Custom branding and themes</li>
                                                <li>• Configurable feature sets</li>
                                                <li>• Custom authentication providers</li>
                                                <li>• Integrated billing systems</li>
                                                <li>• Custom block types and plugins</li>
                                                <li>• Dedicated support channels</li>
                                            </ul>
                                        </div>
                                    </div>
                                </div>
                                
                                <div>
                                    <h3 className="text-lg font-semibold text-orange-400 mb-3">Compliance & Security</h3>
                                    <div className="bg-neutral-800 p-4 rounded">
                                        <div className="grid md:grid-cols-2 gap-4">
                                            <div>
                                                <h4 className="font-semibold text-red-400 mb-2">Data Compliance</h4>
                                                <ul className="text-gray-300 text-sm space-y-1">
                                                    <li>• GDPR compliance tools</li>
                                                    <li>• Data export and portability</li>
                                                    <li>• Right to be forgotten</li>
                                                    <li>• Audit logging and reporting</li>
                                                </ul>
                                            </div>
                                            <div>
                                                <h4 className="font-semibold text-yellow-400 mb-2">Enterprise Security</h4>
                                                <ul className="text-gray-300 text-sm space-y-1">
                                                    <li>• SOC 2 Type II compliance</li>
                                                    <li>• Penetration testing reports</li>
                                                    <li>• Custom security assessments</li>
                                                    <li>• Enhanced access controls</li>
                                                </ul>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Next Steps */}
                        <div className="bg-indigo-500/10 border border-indigo-500/20 rounded-lg p-6">
                            <h3 className="text-lg font-semibold text-white mb-4">
                                🚀 Get Started
                            </h3>
                            <div className="grid md:grid-cols-2 gap-4">
                                <Link href="/support/documentation" className="block p-4 rounded-lg bg-neutral-800/50 hover:bg-neutral-800 transition-colors">
                                    <div className="font-semibold text-cyan-400 mb-1">← Documentation Home</div>
                                    <div className="text-gray-300 text-sm">Return to the main documentation index</div>
                                </Link>
                                <Link href="/support/contact" className="block p-4 rounded-lg bg-neutral-800/50 hover:bg-neutral-800 transition-colors">
                                    <div className="font-semibold text-green-400 mb-1">Contact Support →</div>
                                    <div className="text-gray-300 text-sm">Get help with implementation or custom solutions</div>
                                </Link>
                            </div>
                        </div>
                    </div>
                </motion.div>
            </div>
        </div>
    )
}